
// client/src/components/event/EventAdd.jsx
// Event creation with Native Camera support (deferred upload)

import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import MediaPicker from "../media/MediaPicker";
import NativeCameraCapture from '../media/NativeCameraCapture';
import ImageUploadModal from '../media/ImageUploadModal';
import AppFooter from '../AppFooter';
import TimeSelect from '../TimeSelect';
import { localToUTC, getNextSlotInTenantTZ } from '../../utils/timezoneUtils';
import { FaArrowLeft, FaCamera, FaUpload, FaPlus } from 'react-icons/fa';

function EventAdd() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tenantTimezone, setTenantTimezone] = useState('America/Chicago');
  
  // Separate date and time for better control
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [startTime, setStartTime] = useState('09:00');
  
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [endTime, setEndTime] = useState('17:00');
  
  const [registerStatus, setRegisterStatus] = useState('');
  const [showNativeCamera, setShowNativeCamera] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const [mediaFiles, setMediaFiles] = useState([]);
  const mediaPickerRef = useRef(null);

  const navigate = useNavigate();
  const { tenant } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  /**
   * Handle native camera capture
   * For event creation, we store files locally and upload them when event is created
   * @param {File[]} capturedFiles - Files captured from native camera
   */
  const handleNativeCameraCapture = (capturedFiles) => {
    console.log('📸 Captured files from native camera:', capturedFiles);
    
    setShowNativeCamera(false);
    
    if (!capturedFiles || capturedFiles.length === 0) {
      return;
    }

    // Add captured files to mediaFiles state for later upload
    setMediaFiles(prev => [...prev, ...capturedFiles]);
    
    console.log('✅ Added', capturedFiles.length, 'file(s) to event media');
  };

  const handleUploadComplete = () => {
    setIsImageModalOpen(false);
  };

  const checkDuplicateEvent = async () => {
    const response = await axios.get(`${baseUrl}/api/events/${tenant}/checkname?name=${encodeURIComponent(name)}`);
    return response.data.exists;
  };

  // Fetch tenant timezone on mount
  useEffect(() => {
    const fetchTenantTimezone = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/api/tenants/${tenant}/timezone`,
          { withCredentials: true }
        );
        if (response.data.timezone) {
          setTenantTimezone(response.data.timezone);
          
          // Set default times in tenant timezone
          const { date: nextDate, time: nextTime } = getNextSlotInTenantTZ(response.data.timezone);
          setStartDate(nextDate);
          setStartTime(nextTime);
          
          // End time 1 hour later
          const [h, m] = nextTime.split(':').map(Number);
          const endH = String((h + 1) % 24).padStart(2, '0');
          setEndTime(`${endH}:${m.toString().padStart(2, '0')}`);
        }
      } catch (error) {
        console.error('Error fetching tenant timezone:', error);
      }
    };
    fetchTenantTimezone();
  }, [tenant, baseUrl]);

  const registerEvent = async (e) => {
    e.preventDefault();
    setRegisterStatus('');

    if (!name.trim()) {
      setRegisterStatus(t('events.nameRequired'));
      return;
    }

    // Validate dates and times
    if (!startDate || !startTime) {
      setRegisterStatus(t('events.startRequired'));
      return;
    }

    if (!endDate || !endTime) {
      setRegisterStatus(t('events.endRequired'));
      return;
    }

    try {
      // Check for duplicate event name
      const isDuplicate = await checkDuplicateEvent();
      if (isDuplicate) {
        setRegisterStatus(t('events.duplicateEvent'));
        return;
      }

      // Convert to UTC using the corrected function signature
      // localToUTC expects (dateStr, timeStr, tenantTZ) as THREE separate parameters
      const startUtc = localToUTC(startDate, startTime, tenantTimezone);
      const endUtc = localToUTC(endDate, endTime, tenantTimezone);

      // Validate UTC conversion was successful
      if (!startUtc || startUtc.includes('NaN')) {
        setRegisterStatus(t('events.invalidStart'));
        console.error('Start UTC conversion failed:', { startDate, startTime, tenantTimezone, startUtc });
        return;
      }

      if (!endUtc || endUtc.includes('NaN')) {
        setRegisterStatus(t('events.invalidEnd'));
        console.error('End UTC conversion failed:', { endDate, endTime, tenantTimezone, endUtc });
        return;
      }

      console.log('✅ UTC conversion successful:', { startUtc, endUtc });

      // Create event first
      const eventData = {
        name,
        description,
        type: 'Default',
        startdate: startUtc,  // Backend expects 'startdate', not 'starttime'
        enddate: endUtc,      // Backend expects 'enddate', not 'endtime'
        timezone: tenantTimezone
      };

      console.log('📤 Sending event data:', eventData);

      const eventResponse = await axios.post(
        `${baseUrl}/api/events/${tenant}/registerevent`,
        eventData,
        { withCredentials: true }
      );

      console.log('📥 Full response:', eventResponse);
      console.log('📥 Response data:', eventResponse.data);
      console.log('📥 Response status:', eventResponse.status);

      if (!eventResponse.data?.success) {
        throw new Error(eventResponse.data?.message || 'Event creation failed');
      }

      // Try multiple ways to extract eventId from response
      let eventId = eventResponse.data.eventId 
                 || eventResponse.data.data?.id 
                 || eventResponse.data.data?.eventId
                 || eventResponse.data.id;
                 
      console.log('✅ Event created with ID:', eventId);
      console.log('🔍 eventId type:', typeof eventId);
      console.log('🔍 All possible ID locations:', {
        'data.eventId': eventResponse.data.eventId,
        'data.data?.id': eventResponse.data.data?.id,
        'data.data?.eventId': eventResponse.data.data?.eventId,
        'data.id': eventResponse.data.id
      });

      // Validate we got a valid eventId
      if (!eventId) {
        console.error('❌ No eventId found in response!');
        console.error('❌ Full response.data:', JSON.stringify(eventResponse.data, null, 2));
        
        // Event was created but we can't upload media without the ID
        setRegisterStatus(t('events.noMediaId'));
        
        // Still navigate to events list
        setTimeout(() => navigate(`/${tenant}/events`), 2000);
        return;
      }

      // Upload media files if any
      if (mediaFiles.length > 0 && eventId) {
        console.log('📤 Uploading', mediaFiles.length, 'media file(s)...');
        
        const fd = new FormData();
        mediaFiles.forEach((file) => {
          fd.append('files', file, file.name);
        });

        try {
          await axios.post(
            `${baseUrl}/api/events/${tenant}/events/${eventId}/uploadimage`,
            fd,
            {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' },
            }
          );
          console.log('✅ Media uploaded successfully');
        } catch (uploadError) {
          console.warn('⚠️ Event created but media upload failed:', uploadError);
          console.error('Upload error details:', uploadError.response?.data || uploadError.message);
          // Event was created, so we still navigate but show warning
          alert(t('events.mediaUploadFailed'));
        }
      }

      // Navigate to events list
      navigate(`/${tenant}/events`);
      
    } catch (error) {
      console.error('Error registering event:', error);
      setRegisterStatus(
        error?.response?.data?.message ||
        error?.message ||
        t('events.createError')
      );
    }
  };

  // Helper to create object URL for preview
  const urlFor = (file) => {
    if (!file) return '';
    if (typeof file === 'string') return file;
    if (file instanceof File || file instanceof Blob) {
      return URL.createObjectURL(file);
    }
    return '';
  };

  return (
    <div className="container">
      <h2 className="hdr hdr--primary hdr--lg">{t('events.createEvent')}</h2>
      <form onSubmit={registerEvent}>
        <div className="form-group">
          <label htmlFor="name">{t('events.eventName')}</label>
          <input
            type="text" className="form-control" id="name" name="name"
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder={t('events.enterEventName')} required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">{t('events.eventDescription')}</label>
          <textarea
            className="form-control" id="description" name="description"
            rows={5}
            value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder={t('events.enterEventDescription')}
          />
        </div>

        <div className="form-group">
          <label>{t('events.startDateTime')}</label>
          <div className="row">
            <div className="col-md-6">
              <input
                type="date" 
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div className="col-md-6">
              <TimeSelect
                value={startTime}
                onChange={setStartTime}
                tenantTimezone={tenantTimezone}
                showTimezone={true}
              />
            </div>
          </div>
          <small className="form-hint text-muted">
            ⏰ Times are in 5-minute increments only
          </small>
        </div>

        <div className="form-group">
          <label>{t('events.endDateTime')}</label>
          <div className="row">
            <div className="col-md-6">
              <input
                type="date" 
                className="form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
            </div>
            <div className="col-md-6">
              <TimeSelect
                value={endTime}
                onChange={setEndTime}
                tenantTimezone={tenantTimezone}
                showTimezone={true}
              />
            </div>
          </div>
          <small className="form-hint text-muted">
            ⏰ Times are in 5-minute increments only
          </small>
        </div>

        {/* Media Picker for selecting from device */}
        <MediaPicker ref={mediaPickerRef} onFilesChange={setMediaFiles} />
        
        {/* Preview captured/selected media */}
        {mediaFiles.length > 0 && (
          <div className="form-group">
            <label>{t('events.selectedMedia')} ({mediaFiles.length} {mediaFiles.length !== 1 ? t('common.files') : t('common.file')})</label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', 
              gap: 8, 
              marginTop: 12 
            }}>
              {mediaFiles.map((file, idx) => {
                const src = urlFor(file);
                const isVideo = file.type?.startsWith('video/');
                return (
                  <div key={`${file.name}-${idx}`} style={{ position: 'relative' }}>
                    {isVideo ? (
                      <video src={src} style={{ width: '100%', borderRadius: 8 }} controls />
                    ) : (
                      <img src={src} alt="" style={{ width: '100%', borderRadius: 8 }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </form>

      {registerStatus && (
        <p className="status-message" style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: registerStatus.includes('Error') || registerStatus.includes('failed') || registerStatus.includes('Invalid')
            ? '#fee' 
            : '#efe',
          borderRadius: 4
        }}>
          {registerStatus}
        </p>
      )}

      {/* Native Camera Capture Modal */}
      {showNativeCamera && (
        <NativeCameraCapture
          mode="both"              // Allow both photo and video capture
          multiple={true}          // Allow multiple captures
          maxFiles={10}            // Maximum 10 files per session
          maxImageSizeMB={10}      // Max 10MB per image
          maxVideoSizeMB={100}     // Max 100MB per video
          onCapture={handleNativeCameraCapture}
          onClose={() => setShowNativeCamera(false)}
        />
      )}

      {/* Image Upload Modal (existing functionality) */}
      {isImageModalOpen && (
        <ImageUploadModal
          tenantId={tenant}
          imageName={`${Date.now()}_${tenant}_${name}`}
          onUploadComplete={handleUploadComplete}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}

      {/* Footer Buttons */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: t('common.back'),
            onClick: () => navigate(-1),
          },
          { 
            icon: <FaCamera />, 
            label: t('common.takePhoto'), 
            onClick: () => setShowNativeCamera(true), 
            disabled: false 
          },
          { 
            icon: <FaUpload />, 
            label: t('common.uploadMedia'), 
            onClick: () => mediaPickerRef.current?.open(), 
            disabled: false 
          },
          { 
            icon: <FaPlus />, 
            label: t('common.create'), 
            onClick: registerEvent, 
            type: 'submit' 
          },
        ]}
      />
    </div>
  );
}

export default EventAdd;